import * as Yup from "yup";
import { FormikForm } from "components/FormikForm/FormikForm";
import Modal from "components/DesignSystem/Modal/Modal";
import { Button } from "components/DesignSystem/Button/Button";
import { PriceInput } from "components/PriceInput/PriceInput";
import { TextArea } from "components/DesignSystem/TextArea/TextArea";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { useToast } from "hooks/useToast";
import {
  useCreateFinancialAdjustmentMutation,
  useUpdateFinancialAdjustmentMutation,
} from "store/apis/estimatedTax";
import {
  FinancialAdjustment,
  FinancialAdjustmentMonth,
  CreateFinancialAdjustmentPayload,
} from "types/Models/estimatedTax";
import { BackendError } from "types/utils/error";

const TYPE_OPTIONS = [
  { label: "Income", value: "INCOME" },
  { label: "Expense", value: "EXPENSE" },
];

const validationSchema = Yup.object({
  title: Yup.string().required("Title is required"),
  type: Yup.string().oneOf(["INCOME", "EXPENSE"]).required("Type is required"),
  amount: Yup.number()
    .moreThan(0, "Amount must be greater than 0")
    .required("Amount is required")
    .typeError("Amount must be a number"),
});

type FormValues = {
  title: string;
  type: "INCOME" | "EXPENSE";
  amount: string;
  description: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  entityId: string;
  year: string;
  monthCode: FinancialAdjustmentMonth;
  monthLabel: string;
  adjustment: FinancialAdjustment | null;
  defaultType?: "INCOME" | "EXPENSE";
};

export const FinancialAdjustmentFormModal = ({
  open,
  onClose,
  entityId,
  year,
  monthCode,
  monthLabel,
  adjustment,
  defaultType = "INCOME",
}: Props) => {
  const { alertToast, successToast } = useToast();

  const [createAdj, { isLoading: isCreating }] =
    useCreateFinancialAdjustmentMutation();
  const [updateAdj, { isLoading: isUpdating }] =
    useUpdateFinancialAdjustmentMutation();
  const isSaving = isCreating || isUpdating;

  const initialValues: FormValues = adjustment
    ? {
        title: adjustment.title,
        type: adjustment.type,
        amount: String(adjustment.amount),
        description: adjustment.description ?? "",
      }
    : {
        title: "",
        type: defaultType,
        amount: "",
        description: "",
      };

  const handleSubmit = async (values: FormValues) => {
    const payload: CreateFinancialAdjustmentPayload = {
      year: Number(year),
      month: monthCode,
      amount: Number(values.amount),
      type: values.type,
      title: values.title,
    };
    if (values.description) payload.description = values.description;

    try {
      if (adjustment) {
        await updateAdj({
          entityId,
          adjustmentId: adjustment.id,
          payload,
        }).unwrap();
        successToast({ message: "Adjustment updated" });
      } else {
        await createAdj({ entityId, payload }).unwrap();
        successToast({ message: "Adjustment added" });
      }
      onClose();
    } catch (e) {
      alertToast({
        message:
          (e as BackendError)?.data?.error?.message ||
          "Failed to save adjustment",
      });
    }
  };

  return (
    <Modal.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Modal.Content useCustomOverlay>
        <FormikForm<FormValues>
          key={`${adjustment?.id ?? "new"}-${defaultType}-${monthCode}`}
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, setFieldValue }) => (
            <>
              <Modal.Header>
                <Modal.Title>
                  {adjustment ? "Edit" : "Add"} Financial Adjustment —{" "}
                  {monthLabel}
                </Modal.Title>
                <Modal.Close />
              </Modal.Header>
              <Modal.Body className="t-space-y-4">
                <TextInput name="title" label="Title" required />

                <Combobox
                  label="Type"
                  required
                  options={TYPE_OPTIONS}
                  value={
                    TYPE_OPTIONS.find((o) => o.value === values.type) ?? null
                  }
                  onChange={(option) => {
                    const opt = option as {
                      value: "INCOME" | "EXPENSE";
                    } | null;
                    setFieldValue("type", opt?.value ?? "INCOME");
                  }}
                />

                <PriceInput
                  name="amount"
                  label="Amount"
                  required
                  allowNegative={false}
                />

                <TextArea
                  name="description"
                  label="Description"
                  block
                  placeholder="Optional description"
                />
              </Modal.Body>
              <Modal.FooterButtonGroup>
                <Modal.RawClose asChild>
                  <Button disabled={isSaving}>Cancel</Button>
                </Modal.RawClose>
                <Button
                  type="submit"
                  customType="primary"
                  isLoading={isSaving}
                  disabled={isSaving}
                >
                  {adjustment ? "Save" : "Add"}
                </Button>
              </Modal.FooterButtonGroup>
            </>
          )}
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};
